import type { Language, PlayerProfile, PlayerSlot } from "@marshmallow-duel/simulation";

export interface LocalFighter extends PlayerProfile {
  readonly score: number;
}

export const DEFAULT_LOCAL_FIGHTERS: readonly LocalFighter[] = Object.freeze([
  { id: "local-1", name: "Hráč 1", color: 255, skin: 1, score: 0 },
  { id: "local-2", name: "Hráč 2", color: 16_711_680, skin: 2, score: 0 },
  { id: "local-3", name: "Hráč 3", color: 65_280, skin: 3, score: 0 },
  { id: "local-4", name: "Hráč 4", color: 65_535, skin: 1, score: 0 },
  { id: "local-5", name: "Hráč 5", color: 8_388_863, skin: 1, score: 0 },
  { id: "local-6", name: "Hráč 6", color: 33_023, skin: 2, score: 0 }
]);

const validFighter = (value: unknown, fallback: LocalFighter): LocalFighter => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return fallback;
  const fighter = value as Partial<LocalFighter>;
  return {
    id: fallback.id,
    name: typeof fighter.name === "string" && fighter.name.trim().length > 0 && fighter.name.length <= 24 ? fighter.name : fallback.name,
    color: Number.isInteger(fighter.color) && (fighter.color ?? -1) >= 0 && (fighter.color ?? 16_777_216) <= 0xffffff ? fighter.color as number : fallback.color,
    skin: [1, 2, 3, 4, 31].includes(fighter.skin ?? 0) ? fighter.skin as number : fallback.skin,
    score: Number.isSafeInteger(fighter.score) && (fighter.score ?? -1) >= 0 ? fighter.score as number : fallback.score
  };
};

export const normalizeLocalFighters = (value: unknown): LocalFighter[] => {
  const input = Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? Object.values(value as Record<string, unknown>)
      : [];
  return DEFAULT_LOCAL_FIGHTERS.map((fallback, index) => validFighter(input[index], fallback));
};

export const normalizeFighterSelection = (value: unknown, fighters: readonly LocalFighter[]): string[] => {
  const known = new Set(fighters.map(({ id }) => id));
  const selected = Array.isArray(value) ? value.filter((id): id is string => typeof id === "string" && known.has(id)) : [];
  const unique = [...new Set(selected)];
  for (const fighter of fighters) if (unique.length < 4 && !unique.includes(fighter.id)) unique.push(fighter.id);
  return unique.slice(0, 4);
};

export const localProfilesForMatch = (fighters: readonly LocalFighter[], selection: readonly string[]): Record<PlayerSlot, PlayerProfile> => {
  const profiles: Partial<Record<PlayerSlot, PlayerProfile>> = {};
  for (let index = 0; index < 4; index += 1) {
    const fighter = fighters.find(({ id }) => id === selection[index]) ?? fighters[index] ?? DEFAULT_LOCAL_FIGHTERS[index]!;
    profiles[(index + 1) as PlayerSlot] = { id: fighter.id, name: fighter.name, color: fighter.color, skin: fighter.skin };
  }
  return profiles as Record<PlayerSlot, PlayerProfile>;
};

export const localizeDefaultFighterNames = (fighters: readonly LocalFighter[], language: Language): LocalFighter[] => {
  const prefix = language === "cz" ? "Hráč" : "Player";
  return fighters.map((fighter, index) => /^(Hráč|Player) [1-6]$/.test(fighter.name) ? { ...fighter, name: `${prefix} ${index + 1}` } : fighter);
};
